import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import {
  createLoyaltyCard,
  deactivateLoyaltyCard,
  listLoyaltyCards,
  LoyaltyCard,
  previewLoyaltyMigration,
  updateLoyaltyCard,
} from "../../../../services/loyaltyCardService";
import { getServices } from "../../../../services/serviceService";
import { Service } from "../../../../types/service";

type FormValues = {
  name: string;
  stamps_required: number;
  reward_service_id: string;
  reward_validity_days: string;
  service_ids: number[];
};

const emptyValues: FormValues = {
  name: "",
  stamps_required: 10,
  reward_service_id: "",
  reward_validity_days: "",
  service_ids: [],
};

const LoyaltyCardsPage: React.FC = () => {
  const { t } = useTranslation();
  document.title = t("loyalty_cards.pageTitle");

  const [cards, setCards] = useState<LoyaltyCard[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<LoyaltyCard | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [clientsWithProgress, setClientsWithProgress] = useState(0);
  const [loadingMigrationPreview, setLoadingMigrationPreview] = useState(false);
  const [preservedBanner, setPreservedBanner] = useState<{
    count: number;
    cardName: string;
  } | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setLoadError(null);
    try {
      const [cardsRes, servicesRes] = await Promise.all([
        listLoyaltyCards({ per_page: 100 }),
        getServices({ per_page: 200 }),
      ]);
      setCards(cardsRes.data ?? []);
      setServices(servicesRes.data ?? []);
    } catch {
      setLoadError(t("loyalty_cards.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().required(t("loyalty_cards.validation.name")),
        stamps_required: Yup.number()
          .min(2, t("loyalty_cards.validation.stampsMin"))
          .max(50, t("loyalty_cards.validation.stampsMax"))
          .required(t("loyalty_cards.validation.stamps")),
        reward_service_id: Yup.string().required(
          t("loyalty_cards.validation.reward")
        ),
        service_ids: Yup.array()
          .of(Yup.number())
          .min(2, t("loyalty_cards.validation.servicesMin")),
        reward_validity_days: Yup.string().test(
          "validity",
          t("loyalty_cards.validation.validity"),
          (value) => {
            if (!value || value.trim() === "") {
              return true;
            }
            const n = Number(value);
            return Number.isInteger(n) && n >= 1 && n <= 730;
          }
        ),
      }),
    [t]
  );

  const refreshMigrationPreview = useCallback(
    async (serviceIds: number[], exceptCardId?: number | null): Promise<void> => {
      if (serviceIds.length < 1) {
        setClientsWithProgress(0);
        return;
      }

      setLoadingMigrationPreview(true);
      try {
        const response = await previewLoyaltyMigration({
          service_ids: serviceIds,
          except_card_id: exceptCardId ?? null,
        });
        setClientsWithProgress(response.data?.clients_with_progress ?? 0);
      } catch {
        setClientsWithProgress(0);
      } finally {
        setLoadingMigrationPreview(false);
      }
    },
    []
  );

  const formik = useFormik<FormValues>({
    initialValues: emptyValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      if (clientsWithProgress > 0) {
        const confirmed = window.confirm(
          t("loyalty_cards.confirmMigration", { count: clientsWithProgress })
        );
        if (!confirmed) {
          return;
        }
      }

      setSaving(true);
      try {
        const payload = {
          name: values.name.trim(),
          stamps_required: Number(values.stamps_required),
          reward_service_id: Number(values.reward_service_id),
          reward_validity_days:
            values.reward_validity_days.trim() === ""
              ? null
              : Number(values.reward_validity_days),
          service_ids: values.service_ids,
          is_active: true,
        };

        const response = editing
          ? await updateLoyaltyCard(editing.id, payload)
          : await createLoyaltyCard(payload);

        const migrated = Number(response.data?.migration?.clients_migrated ?? 0);
        const cardName = response.data?.name ?? payload.name;

        if (migrated > 0) {
          toast.success(
            t("loyalty_cards.toast.savedWithMigration", { count: migrated })
          );
          setPreservedBanner({ count: migrated, cardName });
        } else {
          toast.success(
            editing
              ? t("loyalty_cards.toast.updated")
              : t("loyalty_cards.toast.created")
          );
          setPreservedBanner(null);
        }

        setModalOpen(false);
        setEditing(null);
        formik.resetForm();
        setClientsWithProgress(0);
        await load();
      } catch (err) {
        const apiError = err as { message?: string };
        toast.error(apiError.message || t("loyalty_cards.toast.saveError"));
      } finally {
        setSaving(false);
      }
    },
  });

  useEffect(() => {
    if (!modalOpen) {
      return;
    }

    const handle = window.setTimeout(() => {
      void refreshMigrationPreview(
        formik.values.service_ids,
        editing?.id ?? null
      );
    }, 250);

    return () => window.clearTimeout(handle);
  }, [
    modalOpen,
    formik.values.service_ids,
    editing?.id,
    refreshMigrationPreview,
  ]);

  const openCreate = (): void => {
    setEditing(null);
    setClientsWithProgress(0);
    formik.resetForm({ values: emptyValues });
    setModalOpen(true);
  };

  const openEdit = (card: LoyaltyCard): void => {
    setEditing(card);
    setClientsWithProgress(0);
    formik.resetForm({
      values: {
        name: card.name,
        stamps_required: card.stamps_required,
        reward_service_id: String(card.reward_service_id),
        reward_validity_days:
          card.reward_validity_days != null
            ? String(card.reward_validity_days)
            : "",
        service_ids: (card.services ?? []).map((s) => s.id),
      },
    });
    setModalOpen(true);
  };

  const handleDeactivate = async (card: LoyaltyCard): Promise<void> => {
    if (!window.confirm(t("loyalty_cards.confirmDeactivate", { name: card.name }))) {
      return;
    }
    try {
      await deactivateLoyaltyCard(card.id);
      toast.success(t("loyalty_cards.toast.deactivated"));
      await load();
    } catch {
      toast.error(t("loyalty_cards.toast.saveError"));
    }
  };

  const toggleService = (serviceId: number): void => {
    const current = formik.values.service_ids;
    const next = current.includes(serviceId)
      ? current.filter((id) => id !== serviceId)
      : [...current, serviceId];
    formik.setFieldValue("service_ids", next);
    if (
      formik.values.reward_service_id &&
      !next.includes(Number(formik.values.reward_service_id))
    ) {
      formik.setFieldValue("reward_service_id", "");
    }
  };

  const rewardOptions = services.filter((service) =>
    formik.values.service_ids.includes(service.id)
  );

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("loyalty_cards.title")}
          pageTitle={t("menu.catalog")}
        />

        <Row>
          <Col>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                  <div>
                    <h5 className="mb-1">{t("loyalty_cards.title")}</h5>
                    <p className="text-muted mb-0">{t("loyalty_cards.subtitle")}</p>
                  </div>
                  <Button color="primary" onClick={openCreate}>
                    {t("loyalty_cards.new")}
                  </Button>
                </div>

                {preservedBanner ? (
                  <Alert
                    color="success"
                    className="d-flex align-items-center justify-content-between flex-wrap gap-2"
                    toggle={() => setPreservedBanner(null)}
                  >
                    <span>
                      <Badge color="success" className="me-2">
                        {t("loyalty_cards.migration.chip")}
                      </Badge>
                      {t("loyalty_cards.migration.preservedDone", {
                        count: preservedBanner.count,
                        name: preservedBanner.cardName,
                      })}
                    </span>
                  </Alert>
                ) : null}

                {loadError ? <Alert color="danger">{loadError}</Alert> : null}

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : cards.length === 0 ? (
                  <Alert color="info" className="mb-0">
                    <p className="mb-2">{t("loyalty_cards.empty")}</p>
                    <Button color="primary" size="sm" onClick={openCreate}>
                      {t("loyalty_cards.new")}
                    </Button>
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table className="align-middle mb-0">
                      <thead>
                        <tr>
                          <th>{t("loyalty_cards.cols.name")}</th>
                          <th>{t("loyalty_cards.cols.services")}</th>
                          <th>{t("loyalty_cards.cols.stamps")}</th>
                          <th>{t("loyalty_cards.cols.reward")}</th>
                          <th>{t("loyalty_cards.cols.status")}</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {cards.map((card) => (
                          <tr key={card.id}>
                            <td>
                              <strong>{card.name}</strong>
                              {card.reward_validity_days != null ? (
                                <div className="text-muted small">
                                  {t("loyalty_cards.validityDays", {
                                    days: card.reward_validity_days,
                                  })}
                                </div>
                              ) : null}
                            </td>
                            <td>
                              {(card.services ?? [])
                                .map((s) => s.name)
                                .join(", ") || "—"}
                            </td>
                            <td>{card.stamps_required}</td>
                            <td>{card.reward_service?.name ?? "—"}</td>
                            <td>
                              <Badge color={card.is_active ? "success" : "secondary"}>
                                {card.is_active
                                  ? t("loyalty_cards.status.active")
                                  : t("loyalty_cards.status.inactive")}
                              </Badge>
                            </td>
                            <td className="text-end">
                              <Button
                                color="soft-primary"
                                size="sm"
                                className="me-1"
                                onClick={() => openEdit(card)}
                              >
                                {t("loyalty_cards.edit")}
                              </Button>
                              {card.is_active ? (
                                <Button
                                  color="soft-danger"
                                  size="sm"
                                  onClick={() => void handleDeactivate(card)}
                                >
                                  {t("loyalty_cards.deactivate")}
                                </Button>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            void formik.submitForm();
          }}
        >
          <ModalHeader toggle={() => setModalOpen(false)}>
            {editing ? t("loyalty_cards.editTitle") : t("loyalty_cards.newTitle")}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>{t("loyalty_cards.fields.name")}</Label>
              <Input
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={Boolean(formik.touched.name && formik.errors.name)}
              />
              <FormFeedback>{formik.errors.name}</FormFeedback>
            </FormGroup>

            <FormGroup>
              <Label>{t("loyalty_cards.fields.stamps")}</Label>
              <Input
                type="number"
                name="stamps_required"
                min={2}
                max={50}
                value={formik.values.stamps_required}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={Boolean(
                  formik.touched.stamps_required && formik.errors.stamps_required
                )}
              />
              <FormFeedback>{formik.errors.stamps_required}</FormFeedback>
            </FormGroup>

            <FormGroup>
              <Label>{t("loyalty_cards.fields.services")}</Label>
              <p className="text-muted small">{t("loyalty_cards.fields.servicesHint")}</p>
              <div className="border rounded p-2" style={{ maxHeight: 220, overflowY: "auto" }}>
                {services.map((service) => (
                  <FormGroup check key={service.id} className="mb-1">
                    <Input
                      type="checkbox"
                      checked={formik.values.service_ids.includes(service.id)}
                      onChange={() => toggleService(service.id)}
                    />
                    <Label check className="ms-1">
                      {service.name}
                    </Label>
                  </FormGroup>
                ))}
              </div>
              {formik.touched.service_ids && formik.errors.service_ids ? (
                <div className="invalid-feedback d-block">
                  {String(formik.errors.service_ids)}
                </div>
              ) : null}
            </FormGroup>

            {loadingMigrationPreview ? (
              <Alert color="light" className="d-flex align-items-center gap-2">
                <Spinner size="sm" />
                <span>{t("loyalty_cards.migration.loading")}</span>
              </Alert>
            ) : clientsWithProgress > 0 ? (
              <Alert color="info">
                {t("loyalty_cards.migration.preserve", { count: clientsWithProgress })}
              </Alert>
            ) : formik.values.service_ids.length >= 2 ? (
              <Alert color="light" className="mb-3">
                {t("loyalty_cards.migration.none")}
              </Alert>
            ) : null}

            <FormGroup>
              <Label>{t("loyalty_cards.fields.reward")}</Label>
              <Input
                type="select"
                name="reward_service_id"
                value={formik.values.reward_service_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={Boolean(
                  formik.touched.reward_service_id && formik.errors.reward_service_id
                )}
              >
                <option value="">{t("loyalty_cards.fields.rewardPlaceholder")}</option>
                {rewardOptions.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </Input>
              <FormFeedback>{formik.errors.reward_service_id}</FormFeedback>
            </FormGroup>

            <FormGroup>
              <Label>{t("loyalty_cards.fields.validity")}</Label>
              <Input
                type="number"
                name="reward_validity_days"
                min={1}
                max={730}
                placeholder={t("loyalty_cards.fields.validityPlaceholder")}
                value={formik.values.reward_validity_days}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={Boolean(
                  formik.touched.reward_validity_days &&
                    formik.errors.reward_validity_days
                )}
              />
              <FormFeedback>{formik.errors.reward_validity_days}</FormFeedback>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="light" type="button" onClick={() => setModalOpen(false)}>
              {t("loyalty_cards.cancel")}
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" /> : t("loyalty_cards.save")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default LoyaltyCardsPage;
